import '../index.less';

import { Table, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { numberWithCommas } from '../../../helpers/number';
import { getTransactionHistoryThunk } from '../../../services/transactionHistoryService/transactionHistoryThunk';
import { AppDispatch } from '../../../store/store';
import { displayDateAndTime } from '../../../utils/datetime.util';

import type { ColumnsType } from 'antd/es/table/interface';
import type { TablePaginationConfig } from 'antd/es/table';
import { AccountEnquiryResponse } from '../../../features/collection/collectionSlice';
import { transactionDataResponse } from '../../../features/transactionHistory/transactionHistorySlice';

type TransactionHistoryProps = {
  accountEnquiry: AccountEnquiryResponse;
};

const TransactionHistory: React.FC<TransactionHistoryProps> = (props) => {
  const { accountEnquiry } = props;
  const dispatch = useDispatch<AppDispatch>();

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );
  const agreementId = accountEnquiry?.accountDetails?.agreementId;

  const [transactionHistory, setTransactionHistory] = useState<transactionDataResponse | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);

  const tablePagination = {
    total: transactionHistory?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    currentPage: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'No.',
      dataIndex: 'id',
      key: 'id',
      render: (_, __, index: number) =>
        (paginationState.current - 1) * paginationState.pageSize + index + 1,
    },
    {
      title: 'Transaction Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => (text ? displayDateAndTime(text) : '-'),
    },
    {
      title: 'Reference No.',
      dataIndex: 'referenceNo',
      key: 'referenceNo',
      render: (text: string) => (text === '' ? '-' : text),
    },
    {
      title: 'No. of Instalment',
      dataIndex: 'instalmentNo',
      key: 'instalmentNo',
    },
    {
      title: 'Transaction Type',
      dataIndex: 'transactionType',
      key: 'transactionType',
    },
    {
      title: 'Transaction Amount',
      dataIndex: 'transactionAmount',
      key: 'transactionAmount',
      render: (text: number) => (text != null ? `RM ${numberWithCommas(text, 4)}` : '-'),
    },
  ];

  const fetchTransactionHistory = async (params?: any) => {
    setLoading(true);
    await dispatch(getTransactionHistoryThunk({ ...params, agreementId }))
      .unwrap()
      .then((res) => {
        setTransactionHistory(res);
      })
      .catch((err) => {
        message.error('Failed to fetch transaction history');
        console.error('Failed to fetch transaction history', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    fetchTransactionHistory({ ...initialParams, ...filterState, pagination });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useEffect(() => {
    if (agreementId) {
      fetchTransactionHistory(initialParams);
    }
  }, [initialParams, agreementId]);

  return (
    <div className="account-enquiry-container">
      <div className="account-enquiry-section-container">
        <div className="info-container">
          <Table
            columns={columns}
            dataSource={transactionHistory?.data}
            pagination={tablePagination}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
